/**
 * Root App Component
 * Handles routing and authentication state
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { DashboardLayout } from './layouts/DashboardLayout';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { ProjectsPage } from './pages/projects/ProjectsPage';
import { NewProjectPage } from './pages/projects/NewProjectPage';
import { ProjectDetailPage } from './pages/projects/ProjectDetailPage';
import { EditProjectPage } from './pages/projects/EditProjectPage';
import { PaymentsPage } from './pages/payments/PaymentsPage';
import { NewPaymentPage } from './pages/payments/NewPaymentPage';
import { PayrollPage } from './pages/payments/PayrollPage';
import { PaymentRequestsPage } from './pages/payments/PaymentRequestsPage';
import { NewPaymentRequestPage } from './pages/payments/NewPaymentRequestPage';
import { StaffPage } from './pages/staff/StaffPage';
import { NewStaffPage } from './pages/staff/NewStaffPage';
import { EditStaffPage } from './pages/staff/EditStaffPage';
import { StaffDetailPage } from './pages/staff/StaffDetailPage';
import { ContractorsPage } from './pages/contractors/ContractorsPage';
import { NewContractorPage } from './pages/contractors/NewContractorPage';
import { EditContractorPage } from './pages/contractors/EditContractorPage';
import { EmployeesPage } from './pages/employees/EmployeesPage';
import { NewEmployeePage } from './pages/employees/NewEmployeePage';
import { EditEmployeePage } from './pages/employees/EditEmployeePage';
import { DisbursementPage } from './pages/payments/DisbursementPage';
import { PaymentDetailPage } from './pages/payments/PaymentDetailPage';
import { PaymentRequestDetailPage } from './pages/payments/PaymentRequestDetailPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { MapPage } from './pages/map/MapPage';
import { FarmOperationsPage } from './pages/farm/FarmOperationsPage';
import { OperationTasksPage } from './pages/farm/OperationTasksPage';
import { NewFarmOperationPage } from './pages/farm/NewFarmOperationPage';
import { EditFarmOperationPage } from './pages/farm/EditFarmOperationPage';
import { FarmOperationDetailPage } from './pages/farm/FarmOperationDetailPage';
import { ExpensesPage } from './pages/expenses/ExpensesPage';
import { NewExpensePage } from './pages/expenses/NewExpensePage';
import { ExpenseDetailPage } from './pages/expenses/ExpenseDetailPage';
import { EmployeeDetailPage } from './pages/employees/EmployeeDetailPage';
import { ContractorDetailPage } from './pages/contractors/ContractorDetailPage';
import { NotificationsPage } from './pages/notifications/NotificationsPage';
import { LandingPage } from './pages/landing/LandingPage';
import { ProfilePage } from './pages/profile/ProfilePage';
import { ContractsPage } from './pages/contracts/ContractsPage';
import { NewContractPage } from './pages/contracts/NewContractPage';
import { ContractDetailPage } from './pages/contracts/ContractDetailPage';
import { InventoryPage } from './pages/inventory/InventoryPage';

const PrivateRoute = ({ children }: { children: ReactNode }) => {
  const token = localStorage.getItem('jwt_token');
  return token ? children : <Navigate to="/login" replace />;
};


const App = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize app, check authentication, etc.
    const initApp = async () => {
      try {
        // Verify token validity if needed
        const token = localStorage.getItem('jwt_token');
        if (token) {
          // Could verify token here
        }
      } catch (error) {
        console.error('App initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initApp();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <DashboardLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/new" element={<NewProjectPage />} />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        <Route path="projects/:id/edit" element={<EditProjectPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="payments/new" element={<NewPaymentPage />} />
        <Route path="payments/payroll" element={<PayrollPage />} />
        <Route path="payments/requests" element={<PaymentRequestsPage />} />
        <Route path="payments/requests/new" element={<NewPaymentRequestPage />} />
        <Route path="payments/requests/:id" element={<PaymentRequestDetailPage />} />
        <Route path="payments/disbursement" element={<DisbursementPage />} />
        <Route path="payments/:id" element={<PaymentDetailPage />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="staff/new" element={<NewStaffPage />} />
        <Route path="staff/:id" element={<StaffDetailPage />} />
        <Route path="staff/:id/edit" element={<EditStaffPage />} />
        <Route path="contractors" element={<ContractorsPage />} />
        <Route path="contractors/new" element={<NewContractorPage />} />
        <Route path="contractors/:id" element={<ContractorDetailPage />} />
        <Route path="contractors/:id/edit" element={<EditContractorPage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="employees/new" element={<NewEmployeePage />} />
        <Route path="employees/:id" element={<EmployeeDetailPage />} />
        <Route path="employees/:id/edit" element={<EditEmployeePage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="map" element={<MapPage />} />
        <Route path="farm" element={<FarmOperationsPage />} />
        <Route path="farm-tasks" element={<OperationTasksPage />} />
        <Route path="farm/new" element={<NewFarmOperationPage />} />
        <Route path="farm/:id" element={<FarmOperationDetailPage />} />
        <Route path="farm/:id/edit" element={<EditFarmOperationPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="expenses/new" element={<NewExpensePage />} />
        <Route path="expenses/:id" element={<ExpenseDetailPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="contracts" element={<ContractsPage />} />
        <Route path="contracts/new" element={<NewContractPage />} />
        <Route path="contracts/:id" element={<ContractDetailPage />} />
        <Route path="inventory" element={<InventoryPage />} />
      </Route>

      <Route path="*" element={
        localStorage.getItem('jwt_token')
          ? <Navigate to="/dashboard" replace />
          : <Navigate to="/" replace />
      } />
    </Routes>
  );
};

export default App;
